import type React from 'react';
import { createPortal } from 'react-dom';
import { classMerge } from '@/chassis/core/common-helpers/class-merge';
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

type Placement = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';
type TriggerMode = 'hover' | 'click';

type TippyProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
    C,
    {
        content: React.ReactNode;
        getRef?: (el: HTMLElement | null) => void;
        as?: C;
        trigger?: TriggerMode;
        placement?: `${Placement}` | `${Placement}-start` | `${Placement}-end`;
        delay?: number | [number, number];
        /** Fallback for legacy tippy options. */
        options?: {
            trigger?: string;
            placement?: string;
            delay?: number | [number, number];
            theme?: string;
        };
    }
>;

const mapPlacement = (placement?: string): { side: Placement; align: Align } => {
    const raw = placement ?? 'top';
    const [sideRaw, alignRaw] = raw.split('-');
    const side = (sideRaw as Placement) ?? 'top';
    const align: Align = alignRaw === 'start' || alignRaw === 'end' ? (alignRaw as Align) : 'center';
    return { side, align };
};

const toDelay = (delay?: number | [number, number]) => {
    if (Array.isArray(delay)) return delay[0] ?? 0;
    return delay ?? 0;
};

const useHoverDelay = (delayMs: number) => {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
    }, []);
    return {
        start: (fn: () => void) => {
            timer.current && clearTimeout(timer.current);
            timer.current = setTimeout(fn, delayMs);
        },
        cancel: () => {
            timer.current && clearTimeout(timer.current);
            timer.current = null;
        },
    };
};

const Tippy = forwardRef<Element, TippyProps<any>>((props, forwardedRef) => {
    const { content, trigger, placement: placementProp, delay, options, getRef, as, className, children, ...triggerProps } = props;
    const triggerRef = useRef<HTMLElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const Component = (as || 'span') as React.ElementType;
    const [open, setOpen] = useState(false);
    const triggerMode: TriggerMode = useMemo(() => {
        if (trigger) return trigger;
        if (options?.trigger === 'click') return 'click';
        return 'hover';
    }, [trigger, options?.trigger]);

    const placement = useMemo(() => mapPlacement(placementProp ?? options?.placement), [placementProp, options?.placement]);
    const delayDuration = useMemo(() => toDelay(delay ?? options?.delay), [delay, options?.delay]);
    const hoverDelay = useHoverDelay(delayDuration);
    const isLight = options?.theme === 'light';

    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (getRef) {
            getRef(triggerRef.current);
        }
    }, [getRef]);

    const updatePosition = () => {
        const triggerEl = triggerRef.current;
        const overlayEl = overlayRef.current;
        if (!triggerEl || !overlayEl) return;
        const t = triggerEl.getBoundingClientRect();
        const o = overlayEl.getBoundingClientRect();
        const offset = 8;

        let top = t.top;
        let left = t.left;

        switch (placement.side) {
            case 'bottom':
                top = t.bottom + offset;
                left = t.left + t.width / 2 - o.width / 2;
                if (placement.align === 'start') left = t.left;
                if (placement.align === 'end') left = t.right - o.width;
                break;
            case 'left':
                top = t.top + t.height / 2 - o.height / 2;
                if (placement.align === 'start') top = t.top;
                if (placement.align === 'end') top = t.bottom - o.height;
                left = t.left - o.width - offset;
                break;
            case 'right':
                top = t.top + t.height / 2 - o.height / 2;
                if (placement.align === 'start') top = t.top;
                if (placement.align === 'end') top = t.bottom - o.height;
                left = t.right + offset;
                break;
            case 'top':
            default:
                top = t.top - o.height - offset;
                left = t.left + t.width / 2 - o.width / 2;
                if (placement.align === 'start') left = t.left;
                if (placement.align === 'end') left = t.right - o.width;
                break;
        }

        setCoords({ top, left });
        setReady(true);
    };

    useLayoutEffect(() => {
        if (!open) {
            setReady(false);
            return;
        }
        updatePosition();
        const handle = () => updatePosition();
        window.addEventListener('resize', handle);
        window.addEventListener('scroll', handle, true);
        return () => {
            window.removeEventListener('resize', handle);
            window.removeEventListener('scroll', handle, true);
        };
    }, [open, placement]);

    useEffect(() => {
        if (!open || triggerMode !== 'click') return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!target) return;
            if (triggerRef.current?.contains(target)) return;
            if (overlayRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, triggerMode]);

    const hoverHandlers = triggerMode === 'hover'
        ? {
              onMouseEnter: () => hoverDelay.start(() => setOpen(true)),
              onMouseLeave: () => {
                  hoverDelay.cancel();
                  setOpen(false);
              },
              onFocus: () => setOpen(true),
              onBlur: () => setOpen(false),
          }
        : {};

    const commonTriggerProps = {
        ref: (node: HTMLElement | null) => {
            triggerRef.current = node;
            if (typeof forwardedRef === 'function') {
                forwardedRef(node);
            } else if (forwardedRef && 'current' in (forwardedRef as any)) {
                (forwardedRef as React.MutableRefObject<Element | null>).current = node;
            }
        },
        className: classMerge(['cursor-pointer', className]),
        ...triggerProps,
    } as React.ComponentPropsWithoutRef<any>;

    const contentStyles = classMerge([
        'z-50 rounded-md px-3 py-2 text-[0.83rem] shadow-lg will-change-[transform,opacity] border border-slate-200/80 dark:border-darkmode-400',
        isLight ? 'bg-white text-slate-700' : 'bg-slate-800 text-slate-50',
    ]);

    return (
        <>
            <Component
                {...commonTriggerProps}
                {...hoverHandlers}
                onClick={
                    triggerMode === 'click'
                        ? (e: React.MouseEvent) => {
                              commonTriggerProps.onClick?.(e);
                              setOpen((prev) => !prev);
                          }
                        : commonTriggerProps.onClick
                }
            >
                {children}
            </Component>
            {/* Bug fix (Sprint 08/05 — RN-S2.3): renderizar o overlay assim que `open=true`,
                mesmo sem coords ainda. updatePosition() precisa do overlayRef.current pra
                medir o tamanho do tooltip e calcular posicao. Antes, com a guarda
                `open && coords`, o overlay so renderizava DEPOIS de coords ser setado, mas
                coords so era setado por updatePosition que precisava do overlayRef ja
                renderizado — chicken-and-egg que impedia o tooltip de aparecer. Solucao:
                renderizar quando `open`, posicionado offscreen ate as coords serem medidas
                (visibility hidden + top/left distantes ate ready=true). */}
            {open &&
                createPortal(
                    <div
                        ref={overlayRef}
                        style={{
                            position: 'fixed',
                            top: coords?.top ?? -9999,
                            left: coords?.left ?? -9999,
                            zIndex: 9999,
                            visibility: coords && ready ? 'visible' : 'hidden',
                        }}
                    >
                        <div className={contentStyles}>{content}</div>
                    </div>,
                    document.body,
                )}
        </>
    );
});

export default Tippy;
